/**
 * TimelineProtocolCard - Tarjeta de protocolo para vista timeline
 *
 * Diseño simplificado:
 * - Hora ideal prominente
 * - Subtareas siempre visibles (sin expandir)
 * - Menos ruido visual
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Info, Flame, Check, AlertTriangle } from 'lucide-react';
import { useProtocolStore, useUIStore } from '@/store';
import { cn, formatMinutes } from '@/core/utils';
import { getLayerConfig } from '@/core/constants';
import type { Protocol, Subtask } from '@/core/types';
import { Checkbox } from '@/ui/primitives';

interface TimelineProtocolCardProps {
  protocol: Protocol;
}

export function TimelineProtocolCard({ protocol }: TimelineProtocolCardProps) {
  const [showRationale, setShowRationale] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const prevCompletedRef = useRef(protocol.isCompleted);
  const toggleSubtask = useProtocolStore((state) => state.toggleSubtask);
  const preferences = useUIStore((state) => state.preferences);
  const config = getLayerConfig(protocol.layer);

  const completedCount = protocol.subtasks.filter((s) => s.isDone).length;
  const totalCount = protocol.subtasks.length;

  // Detectar cuando el protocolo se acaba de completar
  useEffect(() => {
    if (protocol.isCompleted && !prevCompletedRef.current && preferences.animationsEnabled) {
      setJustCompleted(true);
      const timer = setTimeout(() => setJustCompleted(false), 1500);
      prevCompletedRef.current = protocol.isCompleted;
      return () => clearTimeout(timer);
    }
    prevCompletedRef.current = protocol.isCompleted;
    return undefined;
  }, [protocol.isCompleted, preferences.animationsEnabled]);

  return (
    <motion.div
      className={cn(
        'rounded-lg border transition-colors relative overflow-hidden',
        protocol.isCompleted
          ? 'bg-slate-900/30 border-emerald-800/30'
          : 'bg-slate-900 border-slate-800',
        `border-l-2 ${config.color.border}`
      )}
      animate={justCompleted ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {/* Animación de celebración al completar */}
      <AnimatePresence>
        {justCompleted && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className={cn(
                'absolute inset-0',
                config.color.bgTransparent
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.3, 0] }}
              transition={{ duration: 1 }}
            />
            <motion.div
              className="absolute top-2 right-2"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 10, stiffness: 200 }}
            >
              <div className={cn('p-1 rounded-full', config.color.bg)}>
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Header con hora y título */}
      <div className="flex items-start gap-3 p-3">
        {/* Hora ideal */}
        {protocol.idealTime && (
          <div
            className={cn(
              'flex-shrink-0 text-center min-w-[50px]',
              protocol.isCompleted ? 'opacity-50' : ''
            )}
          >
            <p
              className={cn(
                'text-lg font-bold tabular-nums',
                protocol.isCompleted ? 'text-slate-500' : config.color.text
              )}
            >
              {protocol.idealTime}
            </p>
          </div>
        )}

        {/* Contenido principal */}
        <div className="flex-1 min-w-0">
          {/* Título y meta */}
          <div className="flex items-center gap-2 mb-1">
            {protocol.emoji && (
              <span className="text-base" role="img" aria-hidden="true">
                {protocol.emoji}
              </span>
            )}
            <h3
              className={cn(
                'font-medium text-slate-100',
                protocol.isCompleted && 'line-through text-slate-500'
              )}
            >
              {protocol.title}
            </h3>
            {protocol.streak > 0 && (
              <div className="flex items-center gap-1 text-amber-500">
                <Flame className="w-3 h-3" />
                <span className="text-xs font-medium">{protocol.streak}</span>
              </div>
            )}
          </div>

          {/* Info secundaria */}
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatMinutes(protocol.timeEstimate)}
            </span>
            <span>
              {completedCount}/{totalCount} pasos
            </span>
            {preferences.showRationale && (
              <button
                onClick={() => setShowRationale(!showRationale)}
                className="p-0.5 rounded hover:bg-slate-700 transition-colors"
                aria-label="Ver fundamento"
              >
                <Info className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Rationale */}
          <AnimatePresence>
            {showRationale && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div
                  className={cn(
                    'mt-2 p-2 rounded text-xs',
                    config.color.bgTransparent,
                    config.color.text
                  )}
                >
                  {protocol.rationale}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Subtareas - siempre visibles */}
      <div className="px-3 pb-3 space-y-1">
        {protocol.subtasks.map((subtask: Subtask) => (
          <div key={subtask.id}>
            <label
              className={cn(
                'flex items-start gap-2 p-1.5 rounded cursor-pointer',
                'hover:bg-slate-800/50 transition-colors',
                subtask.isDone && 'opacity-60',
                subtask.isCritical && !subtask.isDone && 'bg-rose-950/20 border-l-2 border-rose-700'
              )}
            >
              <Checkbox
                checked={subtask.isDone}
                onChange={() => toggleSubtask(protocol.id, subtask.id)}
                layer={protocol.layer}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'text-sm text-slate-300',
                      subtask.isDone && 'line-through text-slate-500'
                    )}
                  >
                    {subtask.label}
                  </span>
                  {subtask.isCritical && !subtask.isDone && (
                    <span className="inline-flex items-center gap-1 px-1 py-0.5 text-2xs font-semibold bg-rose-900/40 text-rose-300 rounded border border-rose-700/50">
                      <AlertTriangle className="w-2.5 h-2.5" />
                    </span>
                  )}
                </div>
                {/* Nota científica */}
                {subtask.scientificNote && (
                  <div className="flex items-start gap-1 mt-0.5">
                    <Info className="w-3 h-3 text-slate-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-slate-500 italic">
                      {subtask.scientificNote}
                    </span>
                  </div>
                )}
              </div>
              {subtask.estimatedMinutes && (
                <span className="text-xs text-slate-600 flex-shrink-0">
                  {subtask.estimatedMinutes}m
                </span>
              )}
            </label>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
